<script lang="ts" setup>
import { TRANSACTION_TYPE, type PropertyOnCard } from '@@/shared/property/types';
import { type PropertySearchCriteria, SEARCH_PROPERTY_TYPES_VALUES } from '@@/shared/property/search-criteria';

const props = defineProps<{
  properties: PropertyOnCard[]
  propertySearchCriteria: PropertySearchCriteria
}>();

const emit = defineEmits<{
  filter: [PropertySearchCriteria]
}>();

const modelValue = defineModel<boolean>();

const searchCriteria = toRef(() => props.propertySearchCriteria);
const { modelValue: editableCriteria, syncWithSource } = useCloneOnce(searchCriteria);
watch(modelValue, () => {
  if (modelValue.value) syncWithSource();
});

const filterParams = computed<FilterParams>(() => ({
  propertySearchCriteria: editableCriteria.value,
  properties: props.properties,
}));
const { filtersCount, filteredProperties } = useAgentPropertyFilters(filterParams);
const filteredpropertyCount = computed(() => filteredProperties.value.length);
const hasActiveFilters = computed(() => filtersCount.value > 0);

function resetFilters() {
  editableCriteria.value = {
    ...editableCriteria.value,
    transactionType: undefined,
    propertyTypes: [],
    rooms: [],
  };
};

function applyFilters() {
  emit('filter', editableCriteria.value);
  modelValue.value = false;
}
</script>

<template>
  <IadDialog
    v-model="modelValue"
    modal-name="search_filters"
  >
    <template #top-actions>
      <IadBtn
        variant="link"
        size="fit"
        @click="resetFilters"
      >
        <Icon name="iad-icon:delete" />
        <span v-text="$t('property_serp.filters.clear_all')" />
      </IadBtn>
    </template>
    <template #top-content>
      <IadTitle
        :text="$t(`property_serp.filters.mobile.title`)"
        variant="large"
      />
    </template>
    <div class="px-m pb-l flex flex-col gap-l">
      <div class="flex flex-col gap-xs md:gap-s">
        <IadTitle
          :text="$t(`property_serp.filters.transaction_type`)"
          variant="small-semibold"
        />
        <IadRadioGroup
          v-model="editableCriteria.transactionType"
          :display-error="false"
          class="-mb-l"
        >
          <IadRadio
            :label="$t(`core.navigation.buy`)"
            :value="TRANSACTION_TYPE.SALE"
          />
          <IadRadio
            :label="$t(`core.navigation.rent`)"
            :value="TRANSACTION_TYPE.RENTAL"
          />
        </IadRadioGroup>
      </div>
      <div class="flex flex-col gap-s md:gap-m">
        <IadTitle
          :text="$t(`property_serp.filters.property_type`)"
          variant="small-semibold"
        />
        <IadChipGroup v-model="editableCriteria.propertyTypes">
          <IadChip
            v-for="propertyTypeValue in SEARCH_PROPERTY_TYPES_VALUES"
            :key="propertyTypeValue"
            :label="$t(`property.types_and_sub_types.${propertyTypeValue}`)"
            :value="propertyTypeValue"
          />
        </IadChipGroup>
      </div>
      <div class="flex flex-col gap-s md:gap-m">
        <IadTitle
          :text="$t(`property_serp.filters.rooms`)"
          variant="small-semibold"
        />
        <PropertyFilterInputRoom v-model="editableCriteria.rooms" />
      </div>
    </div>

    <template #actions>
      <IadBtn
        v-if="hasActiveFilters"
        :text="$t('agent.properties.filters.apply', { count: filteredpropertyCount })"
        @click="applyFilters"
      />
      <IadBtn
        v-else
        :text="$t('core.common.search')"
        @click="applyFilters"
      />
    </template>
  </IadDialog>
</template>
